import { useState } from 'react'
import { Search } from 'lucide-react'
import { Drawer } from '@/components/shared/Drawer'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { CreateCollectionFlow } from '@/components/create/collection/CreateCollectionFlow'
import { CollectionList } from './CollectionList'
import { CreateCollectionCTA } from './CreateCollectionCTA'

/** Delay in ms before closing bottom sheet to show visual feedback */
const AUTO_CLOSE_DELAY_MS = 100

interface CollectionSelectorDrawerProps {
    open: boolean
    onClose: () => void
    productId: string
    headerSlot?: React.ReactNode
    onCollectionToggle?: (collectionId: string) => void
    autoCloseOnSelect?: boolean
    title?: string
    onCreateCollection?: () => void
    onSearchChange?: (searchQuery: string) => void
}

export function CollectionSelectorDrawer({
    open,
    onClose,
    productId,
    headerSlot,
    onCollectionToggle,
    autoCloseOnSelect = true,
    title = 'Save to',
    onCreateCollection,
    onSearchChange,
}: CollectionSelectorDrawerProps) {
    const { withAuth } = useProtectedAction()
    const [searchQuery, setSearchQuery] = useState('')
    const [showCreateCollectionFlow, setShowCreateCollectionFlow] =
        useState(false)

    const handleCollectionToggle = (collectionId: string) => {
        if (onCollectionToggle) {
            onCollectionToggle(collectionId)
        }

        if (autoCloseOnSelect) {
            // Close bottom sheet after a brief delay to show the action feedback
            setTimeout(() => {
                onClose()
            }, AUTO_CLOSE_DELAY_MS)
        }
    }

    const handleCreateCollection = () => {
        withAuth(() => {
            onCreateCollection?.()
            setShowCreateCollectionFlow(true)
        })()
    }

    return (
        <>
            <Drawer
                triggerContent={null}
                open={open}
                onOpenChange={() => onClose()}
                heightMode="fixed"
                fixedHeight="95dvh"
                contentClassName="!z-30 !mb-0"
                drawerHeader={{
                    showClose: true,
                    title,
                    showArrow: false,
                    onClose: onClose,
                }}
            >
                <div className="flex flex-1 flex-col min-h-0">
                    {/* Header Slot (e.g., TryOnButton or Product Header) */}
                    {headerSlot && (
                        <div className="px-2 pt-2 shrink-0">
                            {headerSlot}
                        </div>
                    )}

                    {/* Search Input */}
                    <div className="px-2 pt-4 shrink-0">
                        <div className="flex h-10 w-full items-center gap-3 rounded-xl border border-gray-300 px-3">
                            <Search size={16} className="text-gray-400" />
                            <input
                                value={searchQuery}
                                onChange={(e) => {
                                    const newQuery = e.target.value
                                    setSearchQuery(newQuery)
                                    onSearchChange?.(newQuery)
                                }}
                                placeholder="Search"
                                className="flex-1 border-0 bg-white text-sm outline-none placeholder:text-gray-400"
                            />
                        </div>
                    </div>

                    {/* Scrollable Collection List */}
                    <div className="flex-1 overflow-y-auto px-2">
                        <CollectionList
                            productId={productId}
                            searchQuery={searchQuery}
                            onCollectionToggle={handleCollectionToggle}
                        />
                    </div>

                    {/* Sticky Bottom Actions */}
                    <CreateCollectionCTA onClick={handleCreateCollection} />
                </div>
            </Drawer>

            {/* Create Collection Flow */}
            {showCreateCollectionFlow && (
                <CreateCollectionFlow
                    open={showCreateCollectionFlow}
                    onClose={() => setShowCreateCollectionFlow(false)}
                    onCollectionCreated={() => {
                        setShowCreateCollectionFlow(false)
                        setSearchQuery('')
                    }}
                />
            )}
        </>
    )
}
